import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';
import { erApiFeil, erNettfeil } from '../api/feil.ts';
import { useFriskOppHypotese, useHypoteseOvergang } from '../api/useHypoteseBehandling.ts';
import { useHypoteseConfig, useHypoteseMedNummer, type Hypothesis } from '../api/useHypotheses.ts';
import { useMe } from '../api/useMe.ts';
import { useHypoteseProgrammer, type HypothesisProgramReport } from '../api/useHypoteseProgrammer.ts';
import { PROSENTGULV, RAPPORT_ORD } from './Betaprogram.tsx';
import { useSmal } from '../skall/useSmalSkjerm.ts';
import { Ikon } from '../status/Ikon.tsx';
import { HypotesePille } from '../status/Pille.tsx';
import { hypoteseOppforing } from '../status/status.ts';
import { Radiogruppe, Tekstknapp, Varsel } from './deler.tsx';
import { Felt } from './HDetalj.tsx';
import { Skjelettrader } from './HListe.tsx';
import { nummerTekst } from './Kvittering.tsx';
import { hvem, isoDag, navnAv, sendtTilbake, statusNavn, stor } from './lesning.ts';
import { kortDato, kortDatoAv, retningOrd, tall } from './setning.ts';

/**
 * Konklusjonen (H2.2 W11) på /h/{nr}/konklusjon — eierens side når testen går. Bygget 2026-10-27
 * etter tørrkjøringen i H2.5: uten den kunne ingen hypotese nå «konkludert» fra panelet.
 * Wireframen (`hypotese-innsending.jsx`, SideKonklusjon) eier formen og ordlyden: tre utfall som
 * like segmenter, INGEN forhåndsvalgt og ingen farge; resultatet er ett tall med terskelen ved
 * siden av; «Hva vi lærte» og de to obligatoriske radene (DD-35) er felt; «Neste steg» og
 * «Uenighet» er valgfrie men alltid synlige. Én fylt knapp: å lagre konklusjonen er å sende den,
 * og setningen sier hvem som får beskjed. Ctrl+↵ er «Lagre konklusjonen» (DD-11).
 *
 * Kontrakten er `HypothesisConclusionCreate`. Låsene vises FØR kallet, i kontraktens rekkefølge;
 * tjeneren kjører dem også, og et 422 for en rad blir feltets grunn. Statusen står i testes til
 * beslutningstakeren bekrefter. Er konklusjonen sendt tilbake, står den som var i feltene, med
 * merknaden over — tjeneren overskriver samme rad.
 */

const LAERTE_MIN = 40;
const RAD_MAKS = 1000;
const NESTE_MAKS = 500;

type Utfall = 'bekreftet' | 'delvis' | 'avkreftet';
const UTFALL: Array<[Utfall, string]> = [
  ['bekreftet', 'Bekreftet'],
  ['delvis', 'Delvis'],
  ['avkreftet', 'Avkreftet'],
];

/** «43,5» og «43.5» er samme tall; tomt eller noe annet er ikke et tall. */
export function lesTall(tekst: string): number | null {
  const t = tekst.trim().replace(/\s/g, '').replace(',', '.');
  if (!/^-?\d+(\.\d+)?$/.test(t)) return null;
  return Number(t);
}

export function KonklusjonSide({ nr }: { nr: number }) {
  const smal = useSmal();
  const me = useMe();
  const hypotese = useHypoteseMedNummer(nr);
  const tilbake = `/h/til-deg/${nr}`;
  // Når skjemaet først står, blir det stående: en ny henting (etter et 409) skal ikke ta teksten.
  const vist = useRef<string | null>(null);

  const melding = (tittel: string, tekst: string) => (
    <div className="h-flate">
      {smal ? <Stripe tilbake={tilbake} tittel={tittel} /> : null}
      <div className="h-rull">
        <div className="h-innhold h-innhold-les">
          {smal ? null : <h1 className="panel-h1">{tittel}</h1>}
          <p className="hb-dempet">{tekst}</p>
          <div>
            <Link to={tilbake} className="panel-knapp-sekundaer">
              Til deg
            </Link>
          </div>
        </div>
      </div>
    </div>
  );

  if (hypotese.isPending || me.isPending) {
    return (
      <div className="h-flate">
        {smal ? <Stripe tilbake={tilbake} tittel={nummerTekst(nr)} /> : null}
        <Skjelettrader etikett="Henter hypotesen" />
      </div>
    );
  }
  const h = hypotese.data;
  // K2: en hypotese som ikke finnes, sier det samme som en du ikke har tilgang til.
  if (hypotese.isError || !h) {
    return melding(`Fant ikke ${nummerTekst(nr)}`, hypotese.isError && erNettfeil(hypotese.error) ? 'Fikk ikke hentet hypotesen. Sjekk nettet og prøv igjen.' : 'Den finnes ikke, eller den er ikke din å se. Sjekk nummeret.');
  }
  // Tjeneren vokter (403 not_owner); dette er speilet som forklarer først.
  if (h.owner?.id !== me.data?.id) {
    return melding('Konklusjonen er eierens', `${stor(navnAv(h.owner))} skriver konklusjonen; ${navnAv(h.decider)} bekrefter den.`);
  }
  // Ikke i testes, eller bekreftet og fryst: detaljen viser det som gjelder.
  if (vist.current !== h.id && (h.status !== 'testes' || h.conclusion?.frozenAt)) return <Navigate to={tilbake} replace />;
  vist.current = h.id;
  return <Konklusjon h={h} />;
}

function Stripe({ tilbake, tittel }: { tilbake: string; tittel: string }) {
  return (
    <header className="h-stripe" data-testid="hk-stripe">
      <Link to={tilbake} className="panel-stripe-tilbake" aria-label="Tilbake til Til deg">
        <Ikon navn="chevron-back-outline" />
      </Link>
      <h1 className="h-stripe-tittel">{tittel}</h1>
    </header>
  );
}

/** Radene programrapporten kan fylle (H3.4): «Åpne blokkerende funn», «Hva dataene ikke tåler» og «Neste steg». */
type RapportFelt = 'blokk' | 'data' | 'neste';

/** Rapporten som ble brukt, utfallet den foreslo, og verdiene den fylte inn — merket står til hun endrer. */
interface Kilde {
  rapport: HypothesisProgramReport;
  utfall: Utfall;
  felt: Partial<Record<RapportFelt, string>>;
}

/**
 * Det rapporten fyller ut (kontraktens regel på `HypothesisProgramReport`): en liste med blokkerende
 * funn kopieres som linjer, en TOM liste aldri (P5) — «Ingen» krever en setning et menneske skriver.
 * Uten Innspill har rapporten ingen tekster, og bare utfallet fylles. Ingen tall fra rapporten
 * kopieres inn i tekst (DD-16).
 */
function fraRapporten(r: HypothesisProgramReport): Partial<Record<RapportFelt, string>> {
  const ut: Partial<Record<RapportFelt, string>> = {};
  if (r.openBlocking && r.openBlocking.length > 0) ut.blokk = r.openBlocking.join('\n');
  if (r.dataLimitations?.trim()) ut.data = r.dataLimitations.trim();
  if (r.nextSteps?.trim()) ut.neste = r.nextSteps.trim();
  return ut;
}

const RADER_ORD = ['', 'én rad', 'to rader', 'tre rader'];

/** «"Fortsett å se …" · Nordlys Android · 38 svar · fryst 4. des» — under 30 svar «for få til andeler» (DD-16). */
function rapportLinje(r: HypothesisProgramReport, kort: boolean): string {
  const svar = r.responses < PROSENTGULV ? `${r.responses} svar, for få til andeler` : `${r.responses} svar`;
  return [kort ? undefined : r.programName ? `«${r.programName}»` : undefined, kort ? undefined : r.productName, svar, `fryst ${kortDatoAv(r.frozenAt)}`].filter(Boolean).join(' · ');
}

/** 422 for en rad blir feltets grunn; resten står over skjemaet. */
const RAD_KODER: Record<string, 'blokk' | 'data'> = { open_blocking_required: 'blokk', data_limitations_required: 'data' };

function Konklusjon({ h }: { h: Hypothesis }) {
  const smal = useSmal();
  const naviger = useNavigate();
  const megId = useMe().data?.id;
  const config = useHypoteseConfig();
  const overgang = useHypoteseOvergang();
  const friskOpp = useFriskOppHypotese();
  const nr = h.number!;
  const tilbake = `/h/til-deg/${nr}`;
  const k = h.conclusion?.kind === 'konklusjon' ? h.conclusion : undefined;

  const [utfall, setUtfall] = useState<Utfall | undefined>(k?.outcome as Utfall | undefined);
  const [resultat, setResultat] = useState(k?.resultValue != null ? tall(k.resultValue) : '');
  const [maalt, setMaalt] = useState(k?.resultMeasuredOn ?? isoDag(new Date()));
  const [laerte, setLaerte] = useState(k?.learned ?? '');
  const [blokk, setBlokk] = useState(k?.openBlocking ?? '');
  const [data, setData] = useState(k?.dataLimitations ?? '');
  const [neste, setNeste] = useState(k?.nextSteps ?? '');
  const [uenig, setUenig] = useState(k?.dissent ?? '');
  const [feil, setFeil] = useState<string | null>(null);
  const [radFeil, setRadFeil] = useState<{ blokk?: string; data?: string }>({});

  // --- H3.4: programrapporten som forslag (P6) ---------------------------------------------------
  const prg = useHypoteseProgrammer(h.id);
  // De fryste rapportene, nyeste først — én flate per rapport, hver med «Bruk som utgangspunkt».
  const rapporter = useMemo(
    () => (prg.data?.items ?? []).flatMap((p) => (p.report ? [p.report] : [])).sort((a, b) => b.frozenAt.localeCompare(a.frozenAt)),
    [prg.data],
  );
  // Programmene som går ennå: én linje om når rapporten kommer, ingen dato lovet for den (skjerm 21).
  const venter = (prg.data?.items ?? []).filter((p) => !p.report && p.status !== 'avsluttet' && p.status !== 'avbrutt');
  const [kilde, setKilde] = useState<Kilde | null>(null);
  // Skrevet om etter «Send tilbake»: rapporten hun brukte, er fortsatt utgangspunktet.
  const kildeLest = useRef(false);
  useEffect(() => {
    if (kildeLest.current || !k?.programReportId || rapporter.length === 0) return;
    kildeLest.current = true;
    const r = rapporter.find((x) => x.id === k.programReportId);
    if (r) setKilde({ rapport: r, utfall: r.suggestedOutcome as Utfall, felt: fraRapporten(r) });
  }, [k?.programReportId, rapporter]);
  const verdier = { blokk, data, neste };
  const settFelt: Record<RapportFelt, (v: string) => void> = { blokk: setBlokk, data: setData, neste: setNeste };

  /** Ett trykk forhåndsutfyller: utfallet etter fast regel, og radene rapporten har. Ingenting sendes. */
  function bruk(r: HypothesisProgramReport) {
    const fylt: Partial<Record<RapportFelt, string>> = {};
    for (const [felt, v] of Object.entries(fraRapporten(r)) as Array<[RapportFelt, string]>) {
      const naa = verdier[felt];
      // Det hun har skrevet selv, står: bare et tomt felt, eller et som fortsatt er forrige rapports, fylles.
      if (naa.trim() === '' || naa === kilde?.felt[felt]) {
        settFelt[felt](v);
        fylt[felt] = v;
      }
    }
    for (const felt of Object.keys(kilde?.felt ?? {}) as RapportFelt[]) {
      if (!(felt in fylt) && verdier[felt] === kilde!.felt[felt]) settFelt[felt]('');
    }
    setUtfall(r.suggestedOutcome as Utfall);
    setKilde({ rapport: r, utfall: r.suggestedOutcome as Utfall, felt: fylt });
  }

  /** «Tøm det som kom fra rapporten»: det som fortsatt er rapportens, tas ut; det hun har skrevet, står. */
  function toem() {
    if (!kilde) return;
    for (const felt of Object.keys(kilde.felt) as RapportFelt[]) if (verdier[felt] === kilde.felt[felt]) settFelt[felt]('');
    if (utfall === kilde.utfall) setUtfall(undefined);
    setKilde(null);
  }

  /** «fra rapporten» til hun endrer feltet, så «endret fra rapporten» (skjerm 11 og 12). */
  const merke = (felt: RapportFelt): ReactNode => {
    const fra = kilde?.felt[felt];
    if (fra === undefined) return null;
    const lik = verdier[felt] === fra;
    return (
      <span className="hk-merke" data-testid={`hk-merke-${felt}`}>
        <Ikon navn={lik ? 'flask-outline' : 'create-outline'} />
        {lik ? 'fra rapporten' : 'endret fra rapporten'}
      </span>
    );
  };
  // P5: rapportens liste var tom — feltet står tomt, og maskinen skriver ikke en begrunnelse.
  const blokkHint = kilde && kilde.rapport.openBlocking !== undefined && kilde.rapport.openBlocking.length === 0 && blokk.trim() === '';

  const beslutter = navnAv(h.decider);
  const radMin = config.data?.minFieldLength ?? 10;
  const radKort = (v: string) => v.trim().length < radMin;
  const verdi = lesTall(resultat);
  const lengde = laerte.trim().length;

  // Låsene i kontraktens rekkefølge; den første som stenger, er grunnen ved knappen (DD-44).
  const grunn: string | null = !utfall
    ? 'Velg utfallet først.'
    : verdi === null
      ? 'Skriv resultatet som et tall.'
      : !maalt
        ? 'Velg datoen tallet ble målt.'
        : lengde < LAERTE_MIN
          ? `Skriv hva dere lærte — minst ${LAERTE_MIN} tegn.`
          : lengde > RAD_MAKS
            ? `Kort ned «Hva vi lærte» med ${lengde - RAD_MAKS} tegn.`
            : radKort(blokk) || radKort(data)
              ? 'Fyll ut begge radene — «Ingen» med en setning om hvorfor.'
              : blokk.trim().length > RAD_MAKS || data.trim().length > RAD_MAKS || uenig.trim().length > RAD_MAKS
                ? `Kort ned feltet til høyst ${RAD_MAKS} tegn.`
                : neste.trim().length > NESTE_MAKS
                  ? `Kort ned «Neste steg» til høyst ${NESTE_MAKS} tegn.`
                  : null;
  const opptatt = overgang.isPending;

  async function lagre() {
    if (opptatt || grunn || !utfall || verdi === null) return;
    setFeil(null);
    setRadFeil({});
    const body = {
      outcome: utfall,
      resultValue: verdi,
      resultMeasuredOn: maalt,
      learned: laerte.trim(),
      openBlocking: blokk.trim(),
      dataLimitations: data.trim(),
      ...(neste.trim() ? { nextSteps: neste.trim() } : {}),
      ...(uenig.trim() ? { dissent: uenig.trim() } : {}),
      // H3.4: rapporten som ble brukt. Tjeneren fyller ingenting ut — teksten over er hennes.
      ...(kilde ? { programReportId: kilde.rapport.id } : {}),
    };
    try {
      await overgang.mutateAsync({ id: h.id, overgang: { handling: 'conclude', body } });
      void naviger(tilbake, { state: { kvittering: `Konklusjonen er lagret. ${stor(beslutter)} får beskjed.` } });
    } catch (e) {
      if (!erApiFeil(e)) return setFeil('Noe gikk galt. Det du skrev står. Prøv igjen.');
      if (erNettfeil(e)) return setFeil('Ingen nett. Det du skrev står. Prøv igjen når nettet er tilbake.');
      const rad = RAD_KODER[e.code];
      if (rad) return setRadFeil({ [rad]: '«Ingen» krever en setning om hvorfor.' });
      // Noe har endret seg siden siden ble åpnet (status, fryst): hent på nytt.
      friskOpp(h.id, h.number);
      setFeil(e.brukertekst);
    }
  }

  // Ctrl+↵ er «Lagre konklusjonen» (wireframens tastatur), også med fokus i et felt.
  const lagreRef = useRef(lagre);
  lagreRef.current = lagre;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || !(e.ctrlKey || e.metaKey) || e.defaultPrevented) return;
      e.preventDefault();
      void lagreRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const periode = h.testStartsOn && h.testEndsOn ? `testet ${kortDato(h.testStartsOn)}–${kortDato(h.testEndsOn)}` : null;
  const linje = [periode, h.conclusionDueAt ? `konklusjon innen ${kortDatoAv(h.conclusionDueAt)}` : null, `${hvem(h.decider, megId, 'du')} bekrefter`].filter(Boolean).join(' · ');
  const terskel = h.threshold != null && h.direction ? `rett hvis ${retningOrd(h.direction)} ${tall(h.threshold)}${h.unit ? ` ${h.unit}` : ''}` : null;
  const teller = (v: string, maks: number) => (v.length >= maks * 0.8 ? `${v.trim().length} / ${maks}` : undefined);
  const tilbakeSendt = k && sendtTilbake(h) ? k : undefined;

  return (
    <section className="hb-detalj hk-side" aria-labelledby="hk-tittel" data-testid="hk-side">
      {smal ? <Stripe tilbake={tilbake} tittel={nummerTekst(nr)} /> : null}
      <div className="hb-detalj-rull">
        <div className="hb-detalj-innhold hk-innhold">
          {smal ? null : (
            <Link to={tilbake} className="panel-knapp-lenke hb-tilbake">
              <Ikon navn="chevron-back-outline" />
              Til deg
            </Link>
          )}
          {smal ? null : <span className="hb-dempet" data-testid="hk-crumb">{`${h.title ?? ''} / ${nummerTekst(nr)}`}</span>}
          <h1 id="hk-tittel" className="hb-tittel">
            Konklusjonen
          </h1>
          <div className="hb-naa-pille">
            <HypotesePille noekkel="testes" navn={statusNavn(config.data, h) || 'Testes'} />
            <span className="hb-dempet" data-testid="hk-linje">
              {linje}
            </span>
          </div>
          {tilbakeSendt?.sentBackNote ? (
            <div className="ho-info" data-testid="hk-merknad">
              <Ikon navn="return-down-forward-outline" className="ho-info-ikon" />
              <span>{`${stor(navnAv(h.decider))} sendte den tilbake${tilbakeSendt.sentBackAt ? ` ${kortDatoAv(tilbakeSendt.sentBackAt)}` : ''}: «${tilbakeSendt.sentBackNote}»`}</span>
            </div>
          ) : null}
          {feil ? <Varsel>{`Konklusjonen ble ikke lagret. ${feil}`}</Varsel> : null}

          {/* H3.4 (skjerm 10–14, 20, 21): rapporten er et forslag, aldri automatisk. Uten et trykk er
              ingenting fylt ut, og ingenting er valgt (H20). */}
          {prg.isError ? (
            <Varsel tone="info" rolle="status" handling={<Tekstknapp onClick={() => void prg.refetch()}>Prøv igjen</Tekstknapp>}>
              Programrapporten kunne ikke hentes. Konklusjonen kan skrives uten den.
            </Varsel>
          ) : null}
          {kilde ? (
            <div className="hk-rapport hk-rapport-brukt" role="status" data-testid="hk-brukt">
              <Ikon navn="flask-outline" className="hk-rapport-ikon" />
              <div className="hk-rapport-tekst">
                <p className="hk-rapport-spm">{`Fylt ut fra programrapporten — ${['utfallet', RADER_ORD[Object.keys(kilde.felt).length]].filter(Boolean).join(' og ')}. Alt kan endres før du lagrer.`}</p>
                <p className="hb-dempet">{rapportLinje(kilde.rapport, smal)}</p>
                <div className="hk-rapport-knapper">
                  {kilde.rapport.programName ? (
                    <Link to={`/programmer?program=${kilde.rapport.programId}`} className="panel-knapp-lenke">
                      Se rapporten
                    </Link>
                  ) : null}
                  <Tekstknapp onClick={toem}>Tøm det som kom fra rapporten</Tekstknapp>
                </div>
              </div>
            </div>
          ) : null}
          {rapporter
            .filter((r) => r.id !== kilde?.rapport.id)
            .map((r) => (
              <div key={r.id} className="hk-rapport" data-testid={`hk-rapport-${r.programId}`}>
                <Ikon navn="flask-outline" className="hk-rapport-ikon" />
                <div className="hk-rapport-tekst">
                  <p className="hk-rapport-spm">
                    Programrapporten konkluderte: <b>{RAPPORT_ORD[r.conclusion]}</b>. Bruke den som utgangspunkt?
                  </p>
                  <p className="hb-dempet">{rapportLinje(r, smal)}</p>
                  <div className="hk-rapport-knapper">
                    <button type="button" className="panel-knapp-sekundaer hk-rapport-bruk" onClick={() => bruk(r)}>
                      Bruk som utgangspunkt
                    </button>
                    {r.programName && !smal ? (
                      <Link to={`/programmer?program=${r.programId}`} className="panel-knapp-lenke">
                        Se rapporten
                      </Link>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          {rapporter.length === 0
            ? venter.map((p) => (
                <p key={p.id} className="hk-rapport-venter" data-testid="hk-rapport-venter">
                  <Ikon navn="flask-outline" className="hk-rapport-ikon" />
                  <span>{`${p.name ? `Betaprogrammet «${p.name}»` : 'Betaprogrammet'} ${p.closesAt ? `går til ${kortDatoAv(p.closesAt)}` : 'går fortsatt'}. Rapporten kan brukes som utgangspunkt når den er fryst.`}</span>
                </p>
              ))
            : null}

          <div className="h-felt">
            <div className="h-felt-hode">
              <span id="hk-utfall-h" className="hb-felt-etikett">
                Utfall
              </span>
              {kilde ? (
                <span className={utfall === kilde.utfall ? 'hk-merke' : 'hk-merke hk-merke-dempet'} data-testid="hk-merke-utfall">
                  <Ikon navn={utfall === kilde.utfall ? 'flask-outline' : 'create-outline'} />
                  {utfall === kilde.utfall ? `forslag fra rapporten: ${UTFALL.find(([v]) => v === kilde.utfall)?.[1] ?? ''}` : `rapporten sa ${RAPPORT_ORD[kilde.rapport.conclusion]}`}
                </span>
              ) : null}
            </div>
            <Radiogruppe etikettId="hk-utfall-h" valg={UTFALL} aktiv={utfall} onVelg={setUtfall} testid="hk-utfall" />
          </div>
          <div className="hk-resultat">
            <Felt id="hk-res" etikett={`Resultat · ${h.metricName ?? 'måltallet'}`}>
              <span className="hk-tall">
                <input id="hk-res" type="text" inputMode="decimal" className="h-input hk-tall-input" value={resultat} onChange={(e) => setResultat(e.target.value)} autoComplete="off" />
                {terskel ? (
                  <span className="hb-dempet" data-testid="hk-terskel">
                    {terskel}
                  </span>
                ) : null}
              </span>
            </Felt>
            <Felt id="hk-maalt" etikett="Målt">
              <input id="hk-maalt" type="date" className="h-input h-input-dato" value={maalt} onChange={(e) => setMaalt(e.target.value)} />
            </Felt>
          </div>
          <Felt id="hk-laerte" etikett="Hva vi lærte" teller={teller(laerte, RAD_MAKS)}>
            <textarea id="hk-laerte" className="h-input h-tekstfelt" rows={4} value={laerte} onChange={(e) => setLaerte(e.target.value)} />
          </Felt>
          <Felt
            id="hk-blokk"
            etikett="Åpne blokkerende funn"
            teller={teller(blokk, RAD_MAKS)}
            grunn={radFeil.blokk}
            merke={merke('blokk')}
            hint={
              blokkHint ? (
                <p id="hk-blokk-hint" className="hb-dempet" data-testid="hk-blokk-hint">
                  Rapporten hadde ingen blokkerende funn. Skriv «Ingen» med en setning om hvorfor.
                </p>
              ) : null
            }
          >
            <textarea id="hk-blokk" className="h-input h-tekstfelt" rows={2} value={blokk} aria-invalid={radFeil.blokk ? true : undefined} aria-describedby={blokkHint ? 'hk-blokk-hint' : undefined} onChange={(e) => setBlokk(e.target.value)} />
          </Felt>
          <Felt id="hk-data" etikett="Hva dataene ikke tåler" teller={teller(data, RAD_MAKS)} grunn={radFeil.data} merke={merke('data')}>
            <textarea id="hk-data" className="h-input h-tekstfelt" rows={2} value={data} aria-invalid={radFeil.data ? true : undefined} onChange={(e) => setData(e.target.value)} />
          </Felt>
          <Felt id="hk-neste" etikett="Neste steg · valgfritt" teller={teller(neste, NESTE_MAKS)} merke={merke('neste')}>
            <textarea id="hk-neste" className="h-input h-tekstfelt" rows={2} value={neste} placeholder="Hva bør noen gjøre nå?" onChange={(e) => setNeste(e.target.value)} />
          </Felt>
          <Felt id="hk-uenig" etikett="Uenighet · valgfritt" teller={teller(uenig, RAD_MAKS)}>
            <textarea id="hk-uenig" className="h-input h-tekstfelt" rows={2} value={uenig} placeholder="Er noen uenig i lesningen? Skriv det her." onChange={(e) => setUenig(e.target.value)} />
          </Felt>

          <div className="hk-fot">
            <p className="hb-dempet" data-testid="hk-hvem">{`${stor(beslutter)} får beskjed, og bekrefter eller sender den tilbake med en merknad.`}</p>
            <div className="ho-knapper">
              <button
                type="button"
                className="panel-knapp ho-start"
                aria-disabled={Boolean(grunn) || opptatt || undefined}
                aria-busy={opptatt || undefined}
                aria-describedby={grunn ? 'hk-grunn' : undefined}
                aria-keyshortcuts="Control+Enter"
                onClick={() => void lagre()}
                data-testid="hk-lagre"
              >
                <Ikon navn={grunn ? 'lock-closed-outline' : hypoteseOppforing('testes').ikon} />
                {opptatt ? 'Lagrer …' : 'Lagre konklusjonen'}
                {smal || opptatt ? null : <kbd>Ctrl ↵</kbd>}
              </button>
              {grunn ? (
                <span id="hk-grunn" className="h-grunn ho-grunn" data-testid="hk-grunn">
                  <Ikon navn="lock-closed-outline" className="h-grunn-ikon" />
                  <span>{grunn}</span>
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
